import type { Spine } from "@esotericsoftware/spine-pixi-v8";
import type { PresentationTicker } from "../runner/PresentationTicker";

/**
 * Advances every registered `Spine` from the presentation ticker instead of Pixi's shared ticker.
 *
 * Skeletons are ticked from the presentation ticker, so the dev pause / frame-step freezes them together with
 * everything else. Pixi's `Spine` defaults to `autoUpdate` on the SHARED ticker, which our pause
 * doesn't touch — so hosted skeletons are created with `autoUpdate: false` and ticked here with the
 * ticker's raw (unscaled) delta. Per-clip speed is still `state.timeScale`, exactly as before.
 */
export class SpineDriver {
  private readonly spines: Set<Spine> = new Set();
  private readonly unsubscribe: () => void;

  public constructor(ticker: PresentationTicker) {
    this.unsubscribe = ticker.add((dt) => this.tick(dt));
  }

  public attach(spine: Spine): void {
    spine.autoUpdate = false;
    this.spines.add(spine);
  }

  public detach(spine: Spine): void {
    this.spines.delete(spine);
  }

  public tick(deltaSeconds: number): void {
    for (const spine of this.spines) {
      if (spine.destroyed) {
        this.spines.delete(spine);
        continue;
      }
      spine.update(deltaSeconds);
    }
  }

  public get size(): number {
    return this.spines.size;
  }

  public dispose(): void {
    this.unsubscribe();
    this.spines.clear();
  }
}
